import React, { useEffect, useState, useRef } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap"
import { InputText } from "primereact/inputtext"
import { Editor } from "@tinymce/tinymce-react"
import * as Yup from "yup"
import "./EditEmailTemplate.scss"
import { Toast } from "primereact/toast"
import EmailApi from "Api/EmailApi"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"

const EditEmailTemplate = () => {
  document.title = "Edit Email Template"
  const { id } = useParams()
  const location = useLocation()
  const editData = location.state?.data || {}
  const navigate = useNavigate()
  const accessToken = localStorage.getItem("accessToken")

  const [formData, setFormData] = useState({
    title: "",
    subject: "",
    template: "",
  })
  const [loading, setLoading] = useState(true)
  const [saveLoading, setSaveLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const toast = useRef(null)

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    subject: Yup.string().required("Subject is required"),
    template: Yup.string().required("Template content is required"),
  })

  const getEmailTemplate = () => {
    setLoading(true)
    const config = {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }

    EmailApi.getEmailTemplateById(id, config)
      .then(response => {
        setFormData(response.data)
        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Email template fetched successfully",
            life: 3000,
          })
        }
        setLoading(false)
      })
      .catch(error => {
        console.error("Error fetching template:", error)
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail:
              error.response?.data?.message || "Failed to fetch email template",
            life: 3000,
          })
        } // toast.error(
        //   error.response?.data?.message || "Failed to fetch email template",
        // )
        setLoading(false)
      })
  }

  useEffect(() => {
    getEmailTemplate()
  }, [id, accessToken])

  const handleInputChange = (name, value) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined,
      }))
    }
  }

  const handleSubmit = () => {
    setSaveLoading(true)
    validationSchema
      .validate(formData, { abortEarly: false })
      .then(() => {
        // Ensure DOCTYPE is present
        let finalContent = formData.template
        if (!finalContent.includes("<!DOCTYPE")) {
          finalContent = "<!DOCTYPE html>\n" + finalContent
        }

        const config = {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }

        EmailApi.updateEmailTemplate(
          id,
          {
            ...formData,
            template: finalContent,
          },
          config,
        )
          .then(response => {
            if (response.data) {
              setSaveLoading(false)
              setTimeout(() => {
                navigate(-1)
              }, 1000)
              toast.current.show({
                severity: "success",
                summary: "Success",
                detail: "Email template updated successfully",
                life: 3000,
              })
            }
          })
          .catch(error => {
            console.error("Error updating template:", error)
            toast.current.show({
              severity: "error",
              summary: "Error",
              detail:
                error.response?.data?.message ||
                "Failed to update email template",
              life: 3000,
            })
            setSaveLoading(false)
          })
      })
      .catch(err => {
        const validationErrors = {}
        err.inner.forEach(error => {
          validationErrors[error.path] = error.message
        })
        setErrors(validationErrors)
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Please fill all required fields correctly!",
          life: 3000,
        })
        setSaveLoading(false)
      })
  }

  const breadcrumbItems = [
    { label: "Pixwik", link: "/" },
    { label: "Email Template", link: "/EmailTemplateTable" },
    { label: "Edit Email Template", link: "#" },
  ]
  const handleBreadcrumbClick = link => {
    if (link !== "#") {
      navigate(link)
    }
  }

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner />
      </div>
    )
  }

  return (
    <>
      <Toast ref={toast} position="top-right" life="3000" />
      <div className="main-content-container">
        <Row className="h-100">
          <Col xs="12" className="h-100 d-flex flex-column">
            <div className="email-template-header d-flex align-items-center justify-content-between">
              <CustomBreadCrumb
                data={breadcrumbItems}
                pageTitle="Edit Email Template"
                handleClick={handleBreadcrumbClick}
              />
            </div>
            <Card className="mt-2 flex-grow-1 d-flex flex-column">
              <CardBody className="h-100 gap-3">
                <div className="email-template-edit-form">
                  <div className="form-group">
                    <label htmlFor="title" className="label">
                      Title
                    </label>
                    <InputText
                      id="title"
                      value={formData.title}
                      onChange={e => handleInputChange("title", e.target.value)}
                      className="inp-field-box"
                      placeholder="Enter title"
                    />
                    {errors.title && (
                      <small className="error">{errors.title}</small>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="subject" className="label">
                      Subject
                    </label>
                    <InputText
                      id="subject"
                      value={formData.subject}
                      onChange={e =>
                        handleInputChange("subject", e.target.value)
                      }
                      className="inp-field-box"
                      placeholder="Enter subject"
                    />
                    {errors.subject && (
                      <small className="error">{errors.subject}</small>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="template" className="label">
                      HTML Template
                    </label>
                    <Editor
                      apiKey="t7g4d6ut5ygdvwif7tp5bwyysui56413aomjuh8kusvw5qgv"
                      value={formData.template}
                      onEditorChange={content =>
                        handleInputChange("template", content)
                      }
                      init={{
                        height: 500,
                        menubar: true,
                        plugins: [
                          "advlist",
                          "autolink",
                          "lists",
                          "link",
                          "image",
                          "charmap",
                          "preview",
                          "anchor",
                          "code",
                          "fullscreen",
                          "table",
                          "help",
                          "wordcount",
                        ],
                        toolbar:
                          "undo redo | formatselect | " +
                          "bold italic backcolor | alignleft aligncenter " +
                          "alignright alignjustify | bullist numlist outdent indent | " +
                          "removeformat | code | help",
                        // Critical configurations for preserving styles
                        content_css: false,
                        content_style: "",
                        valid_elements: "*[*]", // Allow all elements and attributes
                        extended_valid_elements: "style,link[href|rel]", // Explicitly allow style tags
                        custom_elements: "~style",
                        verify_html: false,
                        cleanup: false,
                        convert_fonts_to_spans: false,
                        remove_trailing_brs: false,
                        force_br_newlines: false,
                        force_p_newlines: false,
                        forced_root_block: false,
                        valid_children: "+body[style],+html[style]",
                        element_format: "html",
                        entities_encode: false, // Prevent entity encoding
                        entity_encoding: "raw", // Keep raw HTML
                        preserve_cdata: true,
                        protect: [
                          /<![\s\S]*?>/g, // Protect DOCTYPE
                          /<\?[\s\S]*?\?>/g, // Protect PHP code
                          /<style[\s\S]*?style>/g, // Protect style tags
                        ],
                        valid_styles: {
                          "*": "*", // Allow all styles on all elements
                        },
                        // Remove automatic modifications
                        paste_as_text: false,
                        paste_enable_default_filters: false,
                        setup: function (editor) {
                          // Preserve content exactly as pasted
                          editor.on("BeforePaste", function (e) {
                            e.preventDefault()
                            const text =
                              e.clipboardData.getData("text/html") ||
                              e.clipboardData.getData("text")
                            editor.execCommand("mceInsertContent", false, text)
                          })
                        },
                      }}
                    />
                    {errors.template && (
                      <small className="error">{errors.template}</small>
                    )}
                  </div>

                  <div className="save-cancel-btn mt-4 d-flex align-items-center justify-content-start gap-3">
                    <Button
                      color="primary"
                      onClick={handleSubmit}
                      disabled={saveLoading}
                    >
                      {saveLoading ? <Spinner size="sm" /> : "Save Changes"}
                    </Button>
                    <Button color="danger" outline onClick={() => navigate(-1)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
        {/* <ToastContainer /> */}
      </div>
    </>
  )
}

export default EditEmailTemplate
